import { convertSymbolBulletsToListOnDom, replaceSymbolBulletsOnDom } from './remove-word-artifacts'
import { validateAgainstVocabularyOnDom } from './validate-against-vocabulary'

/**
 * What: Shared pre-processing for ALL LLM assistant output — runs before the
 *       pipeline branches into bubble rendering or document insert.
 * Why:  Centralises the transforms that both destinations need, so each one
 *       is applied exactly once and in the same order regardless of destination.
 * How:  Single DOM pass:
 *       1. Unwrap full HTML document wrappers (LLM occasionally emits <!doctype>)
 *       2. Convert Symbol-font bullet paragraphs → <ul><li> (structural cleanup)
 *       3. Replace remaining Symbol middle-dots with ● (non-consecutive bullets)
 *       4. Vocabulary gate — drop any class not in the Bayer Named Style set
 *
 * @param options.skipBayerStyledLists  Pass true on the insert path so that
 *   Bayer Named Style list paragraphs are preserved as <p> for Word to resolve.
 *   Bubble path leaves this unset so all Symbol bullets render as visible lists.
 */
export function normalizeAssistantHtml(
  html: string,
  options?: { skipBayerStyledLists?: boolean }
): string {
  if (!html) return html

  const convertOptions = { skipBayerStyled: options?.skipBayerStyledLists }

  if (/<html[\s>]|<body[\s>]|<head[\s>]|<!doctype/i.test(html)) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const body = doc.body
    convertSymbolBulletsToListOnDom(body, convertOptions)
    replaceSymbolBulletsOnDom(body)
    validateAgainstVocabularyOnDom(body)
    return body.innerHTML.trim()
  }

  const template = document.createElement('template')
  template.innerHTML = html
  convertSymbolBulletsToListOnDom(template.content, convertOptions)
  replaceSymbolBulletsOnDom(template.content)

  validateAgainstVocabularyOnDom(template.content)
  const serializer = document.createElement('div')
  serializer.appendChild(template.content)
  return serializer.innerHTML.replace(/<!doctype[^>]*>/gi, '').trim()
}
